import { Ohje } from "./Ohje";
import { t, esim } from "./kieli";
import { useState, useEffect, type CSSProperties } from "react";
import { naytaPvm } from "./pvm";
import { api, type Kayttaja, type RotaatioData, type Rotaatioryhma, type Resurssi } from "./api";

/** Työvuoro- ja rotaatiosuunnittelu. Ryhmä asettaa oletusvuoron; rotaatiojaksot
 *  (pois/päällä) projisoituvat poissaoloksi → kapasiteetti reagoi. */
export function Rotaatio({ kayttaja }: { kayttaja: Kayttaja }) {
  const [data, setData] = useState<RotaatioData | null>(null);
  const [henkilot, setHenkilot] = useState<Resurssi[]>([]);
  const [uusiNimi, setUusiNimi] = useState("");
  const saaMuokata = kayttaja.roolit.some((r) => ["paakayttaja", "suunnittelija"].includes(r));

  const lataa = () => api.rotaatio().then(setData).catch(() => setData(null));
  useEffect(() => { void lataa(); }, []);
  useEffect(() => { api.resurssit().then(setHenkilot).catch(() => setHenkilot([])); }, []);

  async function luoRyhma() {
    if (!uusiNimi.trim()) return;
    await api.luoRotaatioryhma(uusiNimi.trim(), data?.vuorotyypit[0]?.id ?? null);
    setUusiNimi("");
    void lataa();
  }

  if (!data) return <p className="dim">{t("ltar.ladataan")}</p>;
  const vuoroNimi = (id: number | null) =>
    id ? (data.vuorotyypit.find((v) => v.id === id)?.nimi ?? "?") : "—";

  return (
    <>
      <header className="head">
        <div className="head__rivi"><h1>{t("rot.otsikko")}</h1></div>
      </header>

      {/* Ohje ylös (Toni 24.9.2026). Lähde: /api/rotaatio*: luku vaadiRajaamaton, muutokset vaadiRooli(paakayttaja, suunnittelija). Kapasiteettivaikutus 044. */}
      <Ohje
        kuka={<>{t("rot.ohje.kuka")}</>}
        luvut={<>{t("rot.ohje.luvut")}</>} />

      <section className="panel">
        <div className="panel__head"><h2>{t("rot.vuorot")}</h2></div>
        <div style={{ padding: 12, display: "flex", gap: 16, flexWrap: "wrap" }}>
          {data.vuorotyypit.map((v) => (
            <span key={v.id} className="mono">
              {v.nimi}{v.tunnit ? ` · ${v.tunnit} h` : ""}
              {v.tes_lisa_laji ? ` · ${t("rot.lisa", { laji: v.tes_lisa_laji })}` : ""}
            </span>
          ))}
          {data.vuorotyypit.length === 0 && <span className="dim">{t("rot.ei_vuoroja")}</span>}
        </div>
      </section>

      {saaMuokata && (
        <section className="panel">
          <div className="panel__head"><h2>{t("rot.uusi_ryhma")}</h2></div>
          <div style={{ padding: 12, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <label>{t("rot.ryhman_nimi_otsikko")}<input value={uusiNimi} placeholder={esim(t("rot.ryhman_nimi"))}
              onChange={(e) => setUusiNimi(e.target.value)} style={{ minWidth: 260 }} /></label>
            <button className="nappi" onClick={() => void luoRyhma()}>{t("rot.luo_ryhma")}</button>
          </div>
        </section>
      )}

      {data.ryhmat.length === 0 && <p className="dim">{t("rot.ei_ryhmia")}</p>}
      {data.ryhmat.map((r) => (
        <RyhmaKortti key={r.id} ryhma={r} data={data} henkilot={henkilot}
          saaMuokata={saaMuokata} onMuutos={lataa} vuoroNimi={vuoroNimi} />
      ))}
    </>
  );
}

function RyhmaKortti({ ryhma, data, henkilot, saaMuokata, onMuutos, vuoroNimi }: {
  ryhma: Rotaatioryhma; data: RotaatioData; henkilot: Resurssi[];
  saaMuokata: boolean; onMuutos: () => void; vuoroNimi: (id: number | null) => string;
}) {
  const [lisaaHenkilo, setLisaaHenkilo] = useState("");
  const [alkaa, setAlkaa] = useState("");
  const [paattyy, setPaattyy] = useState("");
  const [tila, setTila] = useState<"pois" | "paalla">("pois");
  const [jaksoVuoro, setJaksoVuoro] = useState("");
  const [jaksoKenelle, setJaksoKenelle] = useState("");
  const [virhe, setVirhe] = useState("");

  const jasenIdt = new Set(ryhma.jasenet.map((j) => j.henkilo_id));
  const valittavat = henkilot.filter((h) => !jasenIdt.has(h.id));
  const chip: CSSProperties = {
    display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px",
    border: "1px solid var(--viiva)", fontSize: 13,
  };

  async function lisaaJasen() {
    if (!lisaaHenkilo) return;
    await api.lisaaRotaatioJasen(ryhma.id, Number(lisaaHenkilo));
    setLisaaHenkilo(""); onMuutos();
  }
  async function lisaaJakso() {
    setVirhe("");
    if (!alkaa || !paattyy) { setVirhe(t("rot.anna_alku_loppu")); return; }
    if (paattyy <= alkaa) { setVirhe(t("rot.loppu_jalkeen")); return; }
    try {
      await api.lisaaRotaatioJakso({
        rotaatioryhma_id: ryhma.id,
        henkilo_id: jaksoKenelle ? Number(jaksoKenelle) : null,
        alkaa, paattyy, tila,
        vuorotyyppi_id: jaksoVuoro ? Number(jaksoVuoro) : null,
      });
      setAlkaa(""); setPaattyy(""); setJaksoVuoro(""); setJaksoKenelle(""); onMuutos();
    } catch { setVirhe(t("rot.tallennus_virhe")); }
  }

  return (
    <section className="panel">
      <div className="panel__head">
        <h2>{ryhma.nimi}</h2>
        <span className="dim">{t("rot.ryhma_meta", { vuoro: vuoroNimi(ryhma.oletus_vuorotyyppi_id), n: ryhma.jasenet.length })}</span>
      </div>
      <div style={{ padding: 12, display: "grid", gap: 18 }}>
        <div>
          <h3 className="eyebrow">{t("rot.jasenet")}</h3>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, margin: "8px 0" }}>
            {ryhma.jasenet.map((j) => (
              <span key={j.henkilo_id} style={chip} className="mono">
                {j.lyhenne ?? j.nimi}
                {saaMuokata && (
                  <button aria-label={t("rot.poista_aria")} style={{ border: "none", background: "none", cursor: "pointer", padding: 0 }}
                    onClick={() => void api.poistaRotaatioJasen(ryhma.id, j.henkilo_id).then(onMuutos)}>×</button>
                )}
              </span>
            ))}
            {ryhma.jasenet.length === 0 && <span className="dim">{t("rot.ei_jasenia")}</span>}
          </div>
          {saaMuokata && (
            <div style={{ display: "flex", gap: 6 }}>
              <label>{t("rot.lisaa_jasen_otsikko")}<select value={lisaaHenkilo} onChange={(e) => setLisaaHenkilo(e.target.value)}>
                <option value="">{t("rot.lisaa_jasen")}</option>
                {valittavat.map((h) => (
                  <option key={h.id} value={h.id}>{h.lyhenne ? h.lyhenne + " – " : ""}{h.nimi}</option>
                ))}
              </select></label>
              <button className="nappi nappi--kevyt" onClick={() => void lisaaJasen()}>{t("rot.lisaa")}</button>
            </div>
          )}
        </div>

        <div>
          <h3 className="eyebrow">{t("rot.jaksot")}</h3>
          <table className="selailu__taulu">
            <thead><tr><th>{t("rot.alkaa")}</th><th>{t("rot.paattyy")}</th><th>{t("rot.kenelle")}</th><th>{t("nippu.tila")}</th><th>{t("rot.vuoro")}</th><th></th></tr></thead>
            <tbody>
              {ryhma.jaksot.map((jk) => (
                <tr key={jk.id}>
                  <td className="mono">{naytaPvm(jk.alkaa)}</td>
                  <td className="mono">{naytaPvm(jk.paattyy)}</td>
                  <td>{jk.henkilo_nimi ?? <span className="dim">{t("rot.koko_ryhma_pieni")}</span>}</td>
                  <td>{jk.tila === "pois" ? t("rot.pois") : t("rot.paalla")}</td>
                  <td>{jk.vuorotyyppi_id ? vuoroNimi(jk.vuorotyyppi_id) : <span className="dim">{t("rot.oletus")}</span>}</td>
                  <td>{saaMuokata && (
                    <button className="nappi nappi--kevyt"
                      onClick={() => void api.poistaRotaatioJakso(jk.id).then(onMuutos)}>{t("liite.poista")}</button>
                  )}</td>
                </tr>
              ))}
              {ryhma.jaksot.length === 0 && <tr><td colSpan={6} className="dim">{t("rot.ei_jaksoja")}</td></tr>}
            </tbody>
          </table>
          {saaMuokata && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8, alignItems: "center" }}>
              <label>{t("rot.alkaa")}<input type="date" value={alkaa} onChange={(e) => setAlkaa(e.target.value)} /></label>
              <label>{t("rot.paattyy")}<input type="date" value={paattyy} onChange={(e) => setPaattyy(e.target.value)} /></label>
              <label>{t("rot.kenelle")}<select value={jaksoKenelle} onChange={(e) => setJaksoKenelle(e.target.value)}>
                <option value="">{t("rot.koko_ryhma")}</option>
                {ryhma.jasenet.map((j) => (
                  <option key={j.henkilo_id} value={j.henkilo_id}>{j.lyhenne ?? j.nimi}</option>
                ))}
              </select></label>
              <label>{t("rot.tila_otsikko")}<select value={tila} onChange={(e) => setTila(e.target.value as "pois" | "paalla")}>
                <option value="pois">{t("rot.pois_ei_toissa")}</option>
                <option value="paalla">{t("rot.paalla")}</option>
              </select></label>
              <label>{t("rot.vuoro")}<select value={jaksoVuoro} onChange={(e) => setJaksoVuoro(e.target.value)}>
                <option value="">{t("rot.oletusvuoro")}</option>
                {data.vuorotyypit.map((v) => <option key={v.id} value={v.id}>{v.nimi}</option>)}
              </select></label>
              <button className="nappi" onClick={() => void lisaaJakso()}>{t("rot.lisaa_jakso")}</button>
              {virhe && <span style={{ color: "var(--puna)", fontSize: 13 }}>{virhe}</span>}
            </div>
          )}
        </div>

        {saaMuokata && (
          <div>
            <button className="nappi nappi--kevyt" onClick={() => {
              if (confirm(t("rot.poista_ryhma_kysymys", { nimi: ryhma.nimi })))
                void api.poistaRotaatioryhma(ryhma.id).then(onMuutos);
            }}>{t("rot.poista_ryhma")}</button>
          </div>
        )}
      </div>
    </section>
  );
}
